import { useCallback } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import {
  selectLeadsLatestRegistryHasNextPage,
  selectLeadsLatestRegistryResult,
} from '@app/pages/Leads/model/latestRegistryResultSelectors';
import {
  patchLatestRegistryLeads,
  requestLatestRegistryNextPage,
  requestLatestRegistryRefresh,
  resetLatestRegistryResult,
  setLatestRegistryNextPageLoading,
  setLatestRegistryResult,
  setLatestRegistryResultLoading,
} from '@app/pages/Leads/model/latestRegistryResultSlice';
import { useAppDispatch, useAppSelector } from '@app/store';

interface SetLatestRegistryResultParams {
  leads: ModelLead[];
  totalCount: number | null;
  searchId: string | null;
  searchSignature: string;
  maxSize: number;
  requestedMaxSize: number;
}

export const useLatestRegistryResultBridge = () => {
  const dispatch = useAppDispatch();
  const latestResult = useAppSelector(selectLeadsLatestRegistryResult);
  const hasNextPage = useAppSelector(selectLeadsLatestRegistryHasNextPage);

  const acceptLatestResult = useCallback(
    (params: SetLatestRegistryResultParams) => {
      dispatch(setLatestRegistryResult(params));
    },
    [dispatch]
  );

  const setLoading = useCallback(
    (isLoading: boolean) => {
      dispatch(setLatestRegistryResultLoading(isLoading));
    },
    [dispatch]
  );

  const setNextPageLoading = useCallback(
    (isLoading: boolean) => {
      dispatch(setLatestRegistryNextPageLoading(isLoading));
    },
    [dispatch]
  );

  const requestNextPage = useCallback(() => {
    dispatch(requestLatestRegistryNextPage());
  }, [dispatch]);

  const requestRefresh = useCallback(() => {
    dispatch(requestLatestRegistryRefresh());
  }, [dispatch]);

  const resetLatestResult = useCallback(() => {
    dispatch(resetLatestRegistryResult());
  }, [dispatch]);

  const patchLeads = useCallback(
    (leads: ModelLead[]) => {
      dispatch(patchLatestRegistryLeads(leads));
    },
    [dispatch]
  );

  return {
    ...latestResult,
    hasNextPage,
    acceptLatestResult,
    patchLeads,
    requestNextPage,
    requestRefresh,
    resetLatestResult,
    setLoading,
    setNextPageLoading,
  };
};
